import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { sortBy } from 'lodash';

import query from 'bundles/enterprise-admin-program-catalog/queries/ContractsByOrgIdQuery.graphql';
import type {
  ContractsByOrgId,
  ContractsByOrgIdVariables,
} from 'bundles/enterprise-admin-program-catalog/queries/__generated__/ContractsByOrgId';
import { shouldDisplayContract } from 'bundles/enterprise-admin-program-creation/utils/contract';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

const LIMITED_CONTENT_CONTRACT_TAGS = ['COURSERA_PLATFORM_ONLY', 'PRIVATE_CONTENT_ONLY', 'CAREER_ACADEMY_LIMITED'];

type ContractOption = {
  id: string;
  name: string;
  contractState: string | null;
  isReal: boolean;
  limitType: string | null;
  startsAt: string | null;
  endsAt: string | null;
  utilization: {
    id: string;
    totalEnrollment: number | null;
    totalUserLicense: number | null;
    remainingEnrollment: number | null;
    remainingUserLicense: number | null;
  };
};

export const useContractsByOrgId = () => {
  const thirdPartyOrganization = useThirdPartyOrganization();

  const { data, loading, error } = useQuery<ContractsByOrgId, ContractsByOrgIdVariables>(query, {
    variables: { thirdPartyOrganizationId: thirdPartyOrganization.id },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const contractOptions = useMemo<ContractOption[] | undefined>(() => {
    const contracts = data?.EnterpriseContract?.queryContractsByOrgId.elements;
    if (!contracts) return undefined;

    const filtered = contracts
      .filter((c) => !LIMITED_CONTENT_CONTRACT_TAGS.includes(c.contractTag?.contractTagName ?? ''))
      .filter(shouldDisplayContract);

    return sortBy(
      filtered.map((c) => ({
        id: c.id,
        name: String(c.name),
        contractState: c.contractState,
        isReal: c.isReal,
        limitType: c.limitType,
        startsAt: c.startsAt,
        endsAt: c.endsAt,
        utilization: {
          id: c.utilization.id,
          totalEnrollment: c.utilization.totalEnrollment,
          totalUserLicense: c.utilization.totalUserLicense,
          remainingEnrollment: c.utilization.remainingEnrollment,
          remainingUserLicense: c.utilization.remainingUserLicense,
        },
      })),
      (c) => c.name || c.id
    );
  }, [data]);

  return { contractOptions, loading, error };
};

export default useContractsByOrgId;
